import { customers } from "@fm/api";
import type {
  Customer,
  CreateCustomerPayload,
  ApiResponse,
  ListResponse,
} from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import { getApiBaseUrl } from "../lib/config";

export interface UseCustomersOptions {
  apiKey: string;
  fleetId: string;
  apiBaseUrl?: string;
}

export interface UseCustomersReturn {
  findOrCreateCustomer: (customerData: {
    name: string;
    email: string;
    phone?: string;
  }) => Promise<ApiResponse<Customer>>;
  getCustomer: (customerId: string) => Promise<ApiResponse<Customer>>;
  listCustomers: () => Promise<ApiResponse<Customer[]>>;
}

export function useCustomers(options: UseCustomersOptions): UseCustomersReturn {
  const { apiKey, fleetId, apiBaseUrl } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  const findOrCreateCustomer = async (customerData: {
    name: string;
    email: string;
    phone?: string;
  }) => {
    try {
      // Look the customer up by email on the server. Scanning a page of the
      // list misses anyone beyond it and creates a duplicate.
      const existingCustomer = await customers.findByEmail(
        apiClient,
        fleetId,
        customerData.email,
      );

      if (existingCustomer) {
        return {
          data: existingCustomer,
          success: true,
        };
      }

      // If not found, create new customer
      const createResponse = await customers.create(apiClient, fleetId, {
        name: customerData.name,
        email: customerData.email,
        phone: customerData.phone,
      });

      return createResponse;
    } catch (error) {
      console.error("Error finding or creating customer:", error);
      throw error;
    }
  };

  const getCustomer = async (customerId: string) => {
    return customers.get(apiClient, fleetId, customerId);
  };

  const listCustomers = async () => {
    return customers.list(apiClient, fleetId);
  };

  return {
    findOrCreateCustomer,
    getCustomer,
    listCustomers,
  };
}
